import { Divider, Form, Modal, RadioGroupField, Text } from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import { FeatureFlagsModel } from '@/features/feature-flag/_models/feature-flags';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useFeatureFlagsLocalStorage } from '../_hooks/local-storage';
import { FeatureFlagsLocalStorageValueSchema } from '../_models/dev-tools';
import { getFeatureFlagsLabel } from '../_utils/translation';

type SchemaType = z.infer<typeof FeatureFlagsLocalStorageValueSchema>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const FeatureFlagsModal = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const { storageFeatureFlags, setStorageFeatureFlags } =
    useFeatureFlagsLocalStorage();

  const me = useMe();
  const userId = me.data?.id ?? '';
  const storageValue = storageFeatureFlags?.[userId];

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<SchemaType>({
    defaultValues: storageValue
      ? {
          enabled: storageValue.enabled,
          features: storageValue.features,
          version: 'v2',
        }
      : {
          enabled: false,
          features: {
            comment: false,
            revisionListener: false,
            sharingProject: false,
            workflow: false,
          },
          version: 'v2',
        },
    resolver: zodResolver(FeatureFlagsLocalStorageValueSchema),
  });

  const radioOptions = [true, false].map((value) => ({
    label: value ? 'on' : 'off',
    value,
  }));

  const submitHandler = async (values: SchemaType) => {
    if (userId) {
      setStorageFeatureFlags((localStorageValue) => ({
        ...localStorageValue,
        [userId]: values,
      }));
    }
    window.location.reload();
  };

  return (
    <Modal
      title={t('dev-tools.feature-flags-label')}
      open={isOpen}
      centered
      onOk={handleSubmit(submitHandler)}
      okText={t('update')}
      onCancel={onClose}
      destroyOnClose
    >
      <Text
        variant="footnote"
        weight="regular"
        color="secondary"
        text={t('dev-tools.feature-flag-description')}
      />

      <Divider />

      <Form
        leavePageAlertCondition={null}
        layout="vertical"
        style={{ maxWidth: 480, maxHeight: 400, overflowY: 'scroll' }}
      >
        <RadioGroupField
          name="enabled"
          label={t('dev-tools.feature-flag-mode')}
          control={control}
          errors={errors}
          options={radioOptions}
          required
        />
        {(
          Object.keys(FeatureFlagsModel.shape.features.shape) as Array<
            keyof z.infer<typeof FeatureFlagsModel>['features']
          >
        ).map((key) => (
          <RadioGroupField
            key={key}
            name={`features.${key}`}
            label={getFeatureFlagsLabel(t, key)}
            control={control}
            disabled={!watch('enabled')}
            errors={errors}
            options={radioOptions}
          />
        ))}
      </Form>
    </Modal>
  );
};
